<script>
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import * as XLSX from 'xlsx';
	import { getApi, postApi, delApi } from '../../service/api';
	import { menu, menuSub, menuSubSub, sessionListFilter } from '../../stores';
	import Pagination from '../../components/Pagination.svelte';
	import { formatSeconds, parseLocalTimeMs } from './components/DataParser.js';

	let list = [];
	let users = [];
	let total = 0;
	let totalPages = 1;
	let currentPage = 1;
	let pageSize = 20;
	let loading = false;
	let exporting = false;

	// 필터
	let selectedUserId = '';
	let dateFrom = '';
	let dateTo = '';
	let hideEmpty = true;
	let excludeInternal = true;
	let careMin2h = true; // 엑셀 시트2: 최초케어일 집계 시 2시간 이상 조건 적용 여부
	let showInternalModal = false;
	let internalUsers = [];
	let newInternalUserId = '';
	let newInternalEmail = '';
	let lookupLoading = false;
	let lookupDone = false;
	let lookupError = '';

	// 전구 연결 상태 매핑용: mqtt broker clients
	let mqttClientsMap = {};

	async function loadMqttClients() {
		try {
			const res = await getApi('/mqtt/monitor/broker-status');
			if (res?.clients) {
				mqttClientsMap = {};
				res.clients.forEach(c => {
					if (c.mqttId) {
						mqttClientsMap[c.mqttId] = true;
					}
				});
			}
		} catch (e) {
			console.warn('MQTT clients 로드 실패:', e);
		}
	}

	async function loadInternalUsers() {
		try {
			const res = await getApi('/samsung/poc/internals');
			if (res && res.resultCode === 1000) {
				internalUsers = res.data?.list || [];
			}
		} catch (e) {
			console.error('내부인원 목록 로드 실패:', e);
		}
	}

	async function lookupInternalUser() {
		lookupError = '';
		lookupDone = false;
		if (!newInternalUserId && !newInternalEmail) {
			lookupError = 'ID 또는 이메일을 입력하세요';
			return;
		}
		lookupLoading = true;
		try {
			let url = '/samsung/poc/internals/lookup?';
			if (newInternalUserId) url += `userId=${newInternalUserId}`;
			else url += `email=${encodeURIComponent(newInternalEmail)}`;
			const res = await getApi(url);
			if (res && res.resultCode === 1000) {
				newInternalUserId = String(res.data.userId);
				newInternalEmail = res.data.email;
				lookupDone = true;
			} else {
				lookupError = '사용자를 찾을 수 없습니다';
			}
		} catch (e) {
			lookupError = '조회 실패';
		}
		lookupLoading = false;
	}

	async function addInternalUser() {
		if (!lookupDone) return;
		try {
			await postApi('/samsung/poc/internals', {
				userId: newInternalUserId,
				email: newInternalEmail,
			});
			newInternalUserId = '';
			newInternalEmail = '';
			lookupDone = false;
			await loadInternalUsers();
		} catch (e) {
			console.error('내부인원 추가 실패:', e);
		}
	}

	async function removeInternalUser(user) {
		try {
			await delApi('/samsung/poc/internals', {
				userId: String(user.userId),
				email: user.email,
			});
			await loadInternalUsers();
		} catch (e) {
			console.error('내부인원 삭제 실패:', e);
		}
	}

	function resetLookup() {
		lookupDone = false;
		lookupError = '';
	}

	// 엑셀 컬럼 정의 (순서 고정)
	// _careTime: 프론트에서 timeEnd-timeStart로 계산, SECONDS_COLS: 시분초 변환 대상
	const EXCEL_COLUMNS = [
		// 기본정보
		{ key: 'id',                   header: 'ID' },
		{ key: 'idUser',               header: '사용자ID' },
		{ key: 'userEmail',            header: '이메일' },
		{ key: 'ymd',                  header: '날짜' },
		{ key: 'timeStart',            header: '시작시각' },
		{ key: 'timeEnd',              header: '종료시각' },
		{ key: '_careTime',            header: '총케어시간' },
		// 비교 지표 (Sleepwave → Samsung 교대 배치)
		{ key: 'totalSleepTime',       header: 'SW_TST' },
		{ key: 'samsungTST',           header: 'Sam_TST' },
		{ key: 'sleepEfficiency',      header: 'SW_SE(%)' },
		{ key: 'samsungSE',            header: 'Sam_SE(%)' },
		{ key: 'samsungSPE',           header: 'Sam_SPE(%)' },
		{ key: 'sleepwaveOnsetTime',   header: 'SW_입면시각' },
		{ key: 'samsungOnsetTime',     header: 'Sam_입면시각' },
		{ key: 'sleepLatency',         header: 'SW_SL' },
		{ key: 'samsungSL',            header: 'Sam_SL' },
		{ key: 'wasoTime',             header: 'SW_WASO' },
		{ key: 'samsungWASO',          header: 'Sam_WASO' },
		{ key: 'deepSleepTime',        header: 'SW_Deep' },
		{ key: 'samsungDeep',          header: 'Sam_Deep' },
		{ key: 'lightSleepTime',       header: 'SW_Light' },
		{ key: 'samsungLight',         header: 'Sam_Light' },
		{ key: 'remSleepTime',         header: 'SW_REM' },
		{ key: 'samsungRem',           header: 'Sam_REM' },
		// 기타 지표
		{ key: 'sleepPeriodTime',      header: 'SW_SPT' },
		{ key: 'timeInBed',            header: 'SW_TIB' },
		{ key: 'wakeTime',             header: 'SW_각성시간' },
		{ key: 'wasoCount',            header: 'SW_WASO횟수' },
		// 앱 정보
		{ key: 'region',               header: '리전' },
		{ key: 'packageName',          header: '앱패키지명' },
		{ key: 'appVersion',           header: '앱버전' },
		{ key: 'appVersionCode',       header: '앱버전코드' },
		{ key: 'dartVersion',          header: 'Dart버전' },
		{ key: 'algorithmVersion',     header: '알고리즘버전' },
		// 삼성 매칭 정보
		{ key: 'hasSamsungData',       header: '삼성데이터여부' },
		{ key: 'samsungDataCount',     header: '삼성데이터수(건)' },
	];

	function saveFilter() {
		sessionListFilter.set({ selectedUserId, dateFrom, dateTo, hideEmpty, currentPage });
	}

	async function fetchSessions() {
		loading = true;
		try {
			let url = `/samsung/sessions/${pageSize}/${currentPage}`;
			const params = [];
			if (selectedUserId) params.push(`userId=${selectedUserId}`);
			if (dateFrom) params.push(`dateFrom=${dateFrom}`);
			if (dateTo) params.push(`dateTo=${dateTo}`);
			if (hideEmpty) params.push(`hideEmpty=true`);
			if (excludeInternal) params.push(`excludeInternal=true`);
			if (params.length > 0) url += '?' + params.join('&');

			const res = await getApi(url);
			if (res && res.resultCode === 1000) {
				list = res.data?.list || [];
				total = res.data?.total || 0;
				totalPages = res.data?.totalPage || 1;
				users = res.data?.users || [];
			}
		} catch (e) {
			console.error('세션 목록 로드 실패:', e);
		}
		loading = false;
	}

	function handlePageChange(page) {
		currentPage = page;
		saveFilter();
		fetchSessions();
	}

	function handleFilter() {
		currentPage = 1;
		saveFilter();
		fetchSessions();
	}

	// 초 → HH:MM:SS 변환
	function secsToHMS(s) {
		if (!s && s !== 0) return '';
		const h = Math.floor(s / 3600);
		const m = Math.floor((s % 3600) / 60);
		const sec = Math.floor(s % 60);
		return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
	}

	// 시분초 변환 대상 컬럼 키
	const SECONDS_COLS = new Set([
		'totalSleepTime', 'timeInBed', 'sleepLatency', 'sleepPeriodTime',
		'wasoTime', 'wakeTime', 'deepSleepTime', 'lightSleepTime', 'remSleepTime',
		'samsungTST', 'samsungSL', 'samsungWASO', 'samsungDeep', 'samsungLight', 'samsungRem',
	]);

	async function handleExport() {
		exporting = true;
		try {
			// MQTT 클라이언트 상태 먼저 로드 (전구 연결 유무 판단용)
			await loadMqttClients();

			let url = `/samsung/sessions/export`;
			const params = [];
			if (selectedUserId) params.push(`userId=${selectedUserId}`);
			if (dateFrom) params.push(`dateFrom=${dateFrom}`);
			if (dateTo) params.push(`dateTo=${dateTo}`);
			if (hideEmpty) params.push(`hideEmpty=true`);
			if (excludeInternal) params.push(`excludeInternal=true`);
			if (params.length > 0) url += '?' + params.join('&');

			const res = await getApi(url);
			if (!res || res.resultCode !== 1000) return;

			// 필터 정보 행 (시트 상단)
			const userLabel = selectedUserId
				? (users.find(u => String(u.id) === String(selectedUserId))?.email || selectedUserId)
				: '전체';
			const dateLabel = (dateFrom || dateTo)
				? `${dateFrom || '시작'} ~ ${dateTo || '현재'}`
				: '전체';
			const filterRows = [
				['사용자', userLabel],
				['기간', dateLabel],
				['빈세션숨기기', hideEmpty ? 'O' : 'X'],
				[],
			];

			// 헤더 행
			const headerRow = EXCEL_COLUMNS.map(c => c.header);

			// 데이터 행
			const dataRows = (res.data?.list || []).map(item => {
				const parsedEnd = parseLocalTimeMs(item.timeEnd);
				const parsedStart = parseLocalTimeMs(item.timeStart);
				const careTime = parsedEnd > parsedStart
					? Math.round((parsedEnd - parsedStart) / 1000)
					: 0;
				return EXCEL_COLUMNS.map(col => {
					if (col.key === '_careTime')      return secsToHMS(careTime);
					if (col.key === 'hasSamsungData') return item[col.key] ? 'O' : 'X';
					if (SECONDS_COLS.has(col.key))    return secsToHMS(item[col.key]);
					return item[col.key] ?? '';
				});
			});

			const aoa = [...filterRows, headerRow, ...dataRows];
			const ws = XLSX.utils.aoa_to_sheet(aoa);
			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, '수면세션');
			// 시트2/시트3 에서 공유할 POC 응답
			let pocRes = null;
			// 시트2: POC 이용현황
			try {
				let pocUrl = '/samsung/poc/users';
				const pocParams = [];
				if (excludeInternal) pocParams.push('excludeInternal=true');
				if (!careMin2h) pocParams.push('careMinHours=0');
				if (pocParams.length > 0) pocUrl += '?' + pocParams.join('&');
				pocRes = await getApi(pocUrl);
				if (pocRes && pocRes.resultCode === 1000) {
					const pocHeaders = [
						'사용자ID', '이메일', '닉네임', '가입일',
						'전구시리얼', '전구최초연결일', '현재연결유무', '최초케어일',
					];
					const pocRows = (pocRes.data?.list || []).map(u => {
						const mqttId = 'tasmota_' + u.userId;
						const isOnline = mqttClientsMap[mqttId] ? 'O' : 'X';
						return [
							u.userId, u.email, u.nickName, u.joinedAt,
							u.firstBulbSerial ?? '', u.firstBulbConnectedAt ?? '',
							isOnline, u.firstCareDate ?? '',
						];
					});
					const ws2 = XLSX.utils.aoa_to_sheet([pocHeaders, ...pocRows]);
					XLSX.utils.book_append_sheet(wb, ws2, 'POC 이용현황');
				}
			} catch (e) {
				console.error('POC 이용현황 시트 생성 실패:', e);
			}
			// 시트3: 사용자별 요약
			try {
				if (pocRes && pocRes.resultCode === 1000) {
					const sumHeaders = [
						'사용자ID', '이메일', '닉네임', '가입일',
						'전구시리얼', '케어시작일', '총세션',
						'평균 TST', '평균 SE(%)', '평균 SPE(%)',
					];
					const sumRows = (pocRes.data?.list || []).map(u => [
						u.userId,
						u.email,
						u.nickName,
						u.joinedAt,
						u.firstBulbSerial ?? '',
						u.firstCareDate ?? '',
						u.totalSessions ?? 0,
						u.avgSamsungTst > 0 ? secsToHMS(u.avgSamsungTst) : '',
						u.avgSamsungSE > 0 ? u.avgSamsungSE.toFixed(1) + '%' : '',
						u.avgSpe > 0 ? u.avgSpe.toFixed(1) + '%' : '',
					]);
					const ws3 = XLSX.utils.aoa_to_sheet([sumHeaders, ...sumRows]);
					XLSX.utils.book_append_sheet(wb, ws3, '사용자별 요약');
				}
			} catch (e) {
				console.error('사용자별 요약 시트 생성 실패:', e);
			}
			const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
			XLSX.writeFile(wb, `수면세션_${today}.xlsx`);
		} catch (e) {
			console.error('엑셀 내려받기 실패:', e);
		}
		exporting = false;
	}

	function formatDate(dateStr) {
		if (!dateStr) return '-';
		const d = new Date(dateStr);
		return d.toLocaleDateString('ko-KR');
	}

	function formatDateTime(dateStr) {
		if (!dateStr) return '-';
		const d = new Date(parseLocalTimeMs(dateStr));
		return d.toLocaleString('ko-KR', {
			month: '2-digit', day: '2-digit',
			hour: '2-digit', minute: '2-digit'
		});
	}

	onMount(() => {
		menu.set('삼성 헬스 비교');
		menuSub.set('수면 세션 목록');
		menuSubSub.set('');

		const saved = get(sessionListFilter);
		selectedUserId = saved.selectedUserId;
		dateFrom = saved.dateFrom;
		dateTo = saved.dateTo;
		hideEmpty = saved.hideEmpty;
		currentPage = saved.currentPage;

		fetchSessions();
	});
</script>

<div class="container-xxl flex-grow-1 container-p-y">
	<h4 class="fw-bold py-3 mb-4">수면 세션 목록</h4>

	<!-- 필터 영역 -->
	<div class="card mb-4">
		<div class="card-body">
			<div class="row g-3 align-items-end">
				<div class="col-md-3">
					<label class="form-label" for="userFilter">사용자</label>
					<select id="userFilter" class="form-select form-select-sm" bind:value={selectedUserId}>
						<option value="">전체</option>
						{#each users as user}
							<option value={user.id}>{user.email ? user.email : 'User'} (#{user.id})</option>
						{/each}
					</select>
				</div>
				<div class="col-md-3">
					<label class="form-label" for="dateFromFilter">시작일</label>
					<input id="dateFromFilter" type="date" class="form-control form-control-sm" bind:value={dateFrom} />
				</div>
				<div class="col-md-3">
					<label class="form-label" for="dateToFilter">종료일</label>
					<input id="dateToFilter" type="date" class="form-control form-control-sm" bind:value={dateTo} />
				</div>
				<div class="col-md-3 d-flex align-items-end gap-2">
					<button class="btn btn-primary btn-sm" on:click={handleFilter}>검색</button>
					<button class="btn btn-outline-secondary btn-sm" on:click={() => {
						selectedUserId = ''; dateFrom = ''; dateTo = ''; hideEmpty = true;
						handleFilter();
					}}>초기화</button>
					<div class="form-check form-check-sm ms-2">
						<input class="form-check-input" type="checkbox" id="hideEmptyCheck"
							bind:checked={hideEmpty} on:change={handleFilter} />
						<label class="form-check-label small" for="hideEmptyCheck">빈 세션 숨기기</label>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- 테이블 -->
	<div class="card">
		<div class="card-header d-flex justify-content-between align-items-center">
			<h5 class="mb-0">총 {total}건</h5>
			<div class="d-flex align-items-center gap-2">
				<div class="form-check form-check-sm">
					<input class="form-check-input" type="checkbox" id="excludeInternalCheck"
						bind:checked={excludeInternal} on:change={handleFilter} />
					<label class="form-check-label small" for="excludeInternalCheck">내부인원 제외</label>
				</div>
				<button class="btn btn-outline-secondary btn-sm" on:click={() => { showInternalModal = true; loadInternalUsers(); }}>
					<i class="bx bx-user-x me-1"></i>내부인원 관리
				</button>
				<div class="form-check form-check-sm" title="시트2 최초케어일: 체크 시 2시간 이상 세션만, 해제 시 모든 세션 기준">
					<input class="form-check-input" type="checkbox" id="careMin2hCheck" bind:checked={careMin2h} />
					<label class="form-check-label small" for="careMin2hCheck">케어 2h+ 기준</label>
				</div>
				<button class="btn btn-success btn-sm" on:click={handleExport} disabled={exporting}>
					{#if exporting}
						<span class="spinner-border spinner-border-sm me-1" role="status"></span>내려받는 중...
					{:else}
						<i class="bx bx-download me-1"></i>엑셀 내려받기
					{/if}
				</button>
			</div>
		</div>
		<div class="table-responsive text-nowrap">
			<table class="table table-hover table-sm">
				<thead class="table-light">
					<tr>
						<th>ID</th>
						<th>사용자</th>
						<th>날짜</th>
						<th>시작</th>
						<th>종료</th>
						<th>총수면시간</th>
						<th>효율</th>
						<th>Deep</th>
						<th>Light</th>
						<th>REM</th>
						<th>Samsung</th>
						<th>상세</th>
					</tr>
				</thead>
				<tbody>
					{#if loading}
						<tr>
							<td colspan="12" class="text-center py-4">
								<div class="spinner-border spinner-border-sm" role="status"></div>
								<span class="ms-2">로딩중...</span>
							</td>
						</tr>
					{:else if list.length === 0}
						<tr>
							<td colspan="12" class="text-center py-4 text-muted">데이터가 없습니다</td>
						</tr>
					{:else}
						{#each list as item}
							<tr>
								<td>{item.id}</td>
								<td>
									<a href="/samsung/compare/{item.id}" class="text-decoration-none">
										{item.userEmail || '-'} <small class="text-muted">#{item.idUser}</small>
									</a>
								</td>
								<td>{formatDate(item.ymd)}</td>
								<td>{formatDateTime(item.timeStart)}</td>
								<td>{formatDateTime(item.timeEnd)}</td>
								<td class:text-muted={!item.totalSleepTime}>{item.totalSleepTime ? formatSeconds(item.totalSleepTime) : '-'}</td>
								<td>{item.sleepEfficiency ? item.sleepEfficiency.toFixed(1) + '%' : '-'}</td>
								<td>{item.deepSleepRate ? item.deepSleepRate.toFixed(1) + '%' : '-'}</td>
								<td>{item.lightSleepRate ? item.lightSleepRate.toFixed(1) + '%' : '-'}</td>
								<td>{item.remSleepRate ? item.remSleepRate.toFixed(1) + '%' : '-'}</td>
								<td>
									{#if item.hasSamsungData}
										<span class="badge bg-label-success">
											<i class="bx bx-check"></i>
										</span>
									{:else}
										<span class="badge bg-label-secondary">
											<i class="bx bx-minus"></i>
										</span>
									{/if}
								</td>
								<td>
									<a href="/samsung/compare/{item.id}" class="btn btn-sm btn-outline-primary">
										<i class="bx bx-bar-chart-alt-2 me-1"></i>비교
									</a>
									<a href="/samsung/lights/{item.id}" class="btn btn-sm btn-outline-info ms-1">
										<i class="bx bx-bulb me-1"></i>조명
									</a>
								</td>
							</tr>
						{/each}
					{/if}
				</tbody>
			</table>
		</div>
		<Pagination {currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
	</div>
</div>

<!-- 내부인원 관리 모달 -->
{#if showInternalModal}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<div class="modal-backdrop fade show" on:click={() => showInternalModal = false}></div>
	<div class="modal fade show d-block" tabindex="-1">
		<div class="modal-dialog">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">내부인원 관리</h5>
					<button type="button" class="btn-close" on:click={() => showInternalModal = false}></button>
				</div>
				<div class="modal-body">
					<table class="table table-sm table-bordered mb-3">
						<thead class="table-light">
							<tr>
								<th>사용자ID</th>
								<th>이메일</th>
								<th style="width:60px"></th>
							</tr>
						</thead>
						<tbody>
							{#if internalUsers.length === 0}
								<tr><td colspan="3" class="text-center text-muted py-3">등록된 내부인원이 없습니다</td></tr>
							{:else}
								{#each internalUsers as user}
									<tr>
										<td>{user.userId}</td>
										<td>{user.email}</td>
										<td>
											<button class="btn btn-sm btn-outline-danger" on:click={() => removeInternalUser(user)}>
												<i class="bx bx-trash"></i>
											</button>
										</td>
									</tr>
								{/each}
							{/if}
						</tbody>
					</table>
					<hr />
					<p class="small text-muted mb-2">ID 또는 이메일 중 하나를 입력하고 짝맞추기를 누르세요</p>
					<div class="row g-2 align-items-end">
						<div class="col-3">
							<label class="form-label small">사용자ID</label>
							<input type="text" class="form-control form-control-sm" bind:value={newInternalUserId}
								placeholder="ID" disabled={lookupDone} on:input={resetLookup} />
						</div>
						<div class="col-5">
							<label class="form-label small">이메일</label>
							<input type="text" class="form-control form-control-sm" bind:value={newInternalEmail}
								placeholder="email" disabled={lookupDone} on:input={resetLookup} />
						</div>
						<div class="col-4 d-flex gap-1">
							{#if !lookupDone}
								<button class="btn btn-outline-primary btn-sm w-100" on:click={lookupInternalUser} disabled={lookupLoading}>
									{lookupLoading ? '조회중...' : '짝맞추기'}
								</button>
							{:else}
								<button class="btn btn-primary btn-sm w-100" on:click={addInternalUser}>등록</button>
							{/if}
						</div>
					</div>
					{#if lookupError}
						<div class="text-danger small mt-1">{lookupError}</div>
					{/if}
					{#if lookupDone}
						<div class="text-success small mt-1">짝맞추기 완료 — 등록 버튼을 눌러주세요</div>
					{/if}
				</div>
			</div>
		</div>
	</div>
{/if}
